import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAuthToken } from '../utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [plan, setPlan] = useState('');

  useEffect(() => {
    processPayment();
  }, []);

  const processPayment = async () => {
    const paymentId = searchParams.get('paymentId');
    const payerId = searchParams.get('PayerID');
    const planParam = searchParams.get('plan');
    const token = getAuthToken();

    if (!paymentId || !payerId || !token) {
      toast.error('Invalid payment session');
      navigate('/dealer/register');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/success?payment_id=${paymentId}&payer_id=${payerId}&plan=${planParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setSuccess(true);
        setPlan(response.data.plan);
        toast.success('Subscription activated successfully!');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
      navigate('/dealer/register');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#004E86] mx-auto mb-4" size={48} />
          <p className="text-lg text-gray-700">Processing your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12" data-testid="subscription-success">
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-xl rounded-2xl border-t-4 border-green-500 p-8 text-center">
          <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
          
          <h1 
            className="text-3xl font-bold text-[#004E86] mb-4"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Welcome to RV Match AI!
          </h1>
          
          <p className="text-lg text-gray-700 mb-6">
            Your <span className="font-bold capitalize">{plan}</span> subscription is now active.
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 font-medium">What's next?</p>
            <ul className="text-green-700 text-sm mt-2 space-y-1">
              <li>• Access your personalized dealer dashboard</li>
              <li>• Share your unique quiz link with customers</li>
              <li>• Start receiving qualified leads</li>
              <li>• Add your RV inventory</li>
            </ul>
          </div>
          
          <Button
            onClick={() => navigate('/dealer')}
            className="w-full bg-[#F2A900] text-black hover:bg-[#D99700] font-bold uppercase tracking-wide py-3"
            data-testid="go-to-dashboard"
          >
            Go to My Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
